import React from 'react'
import { useInView } from 'react-intersection-observer'
import { GraduationCap, Target, Code, MapPin, Check, Calendar } from 'lucide-react'

const iconMap = {
  GraduationCap: GraduationCap,
  Target: Target,
  Code: Code,
}

const EducationCard = ({ education, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const IconComponent = iconMap[education.icon] || GraduationCap

  const gradeClass = (g) => {
    const k = (g || '').toUpperCase()
    if (k === 'A') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
    if (k === 'B') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
    if (k === 'C') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
    if (k === 'S') return 'bg-slate-200 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'
    if (k === 'W' || k === 'F') return 'bg-rose-100 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300'
    if (k.includes('DISTINCTION')) return 'bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300'
    if (k.includes('MERIT')) return 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300'
    return 'bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft'
  }

  const chipClass = (variant) => {
    switch ((variant || 'neutral').toLowerCase()) {
      case 'success':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 border-emerald-300/40 dark:border-emerald-700/40'
      case 'info':
        return 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300 border-sky-300/40 dark:border-sky-700/40'
      case 'warning':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border-amber-300/40 dark:border-amber-700/40'
      case 'accent':
        return 'bg-navy-medium/10 text-navy-medium dark:text-navy-light border-navy-medium/30'
      default:
        return 'bg-navy-pale dark:bg-navy-base text-navy-base dark:text-navy-soft border-navy-soft/60 dark:border-navy-base'
    }
  }

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 delay-${index * 100} ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="relative bg-white dark:bg-navy-deep rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 overflow-hidden group border-2 border-transparent hover:border-accent/30">
        {/* Decorative Background Pattern */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-navy-medium/10 to-navy-deep/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20 group-hover:scale-150 transition-transform duration-700"></div>
        
        {/* Content Container - 2 Column Layout */}
        <div className="relative p-6">
          <div className="grid md:grid-cols-[auto_1fr] gap-6">
            {/* Left Column - Icon & Duration */}
            <div className="flex flex-col items-center gap-4">
              {/* Animated Icon */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-navy-medium to-navy-deep rounded-3xl blur-lg opacity-40 group-hover:opacity-70 transition-all duration-500 animate-pulse"></div>
                <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-navy-medium via-navy-light to-navy-deep flex items-center justify-center shadow-2xl transform group-hover:scale-110 group-hover:-rotate-12 transition-all duration-500">
                  <IconComponent className="w-10 h-10 text-white" strokeWidth={2} />
                </div>
              </div>
              
              {/* Duration Badge */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-navy-medium to-navy-deep rounded-full blur opacity-50"></div>
                <span className="relative block text-center text-xs font-bold text-white bg-gradient-to-r from-navy-medium to-navy-deep px-4 py-2 rounded-full whitespace-nowrap shadow-lg">
                  {education.period || education.duration}
                </span>
              </div>
            </div>

            {/* Right Column - Content */}
            <div className="flex flex-col">
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-navy-dark dark:text-navy-lightest mb-1 leading-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-navy-medium group-hover:to-navy-deep transition-all duration-300">
                  {education.degree}
                </h3>
                {education.subtitle && (
                  <p className="text-sm font-semibold text-navy-medium dark:text-accent mb-1">{education.subtitle}</p>
                )}
                <p className="text-lg font-semibold text-navy-medium dark:text-accent mb-2">
                  {education.institution}
                </p>
                {education.system && (
                  <p className="text-sm text-navy-base dark:text-navy-soft mb-2">{education.system}</p>
                )}

                {/* Meta Row: Time, Location, Chips */}
                <div className="flex flex-wrap items-center gap-2 text-sm text-navy-base dark:text-navy-soft">
                  {(education.period || education.duration) && (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-navy-medium" />
                      <span className="font-medium">{education.period || education.duration}</span>
                    </span>
                  )}
                  {education.location && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-navy-medium" />
                      <span className="font-medium">{education.location}</span>
                    </span>
                  )}
                  {Array.isArray(education.chips) && education.chips.length > 0 && (
                    education.chips.map((chip, idx) => {
                      const label = typeof chip === 'string' ? chip : chip.label
                      const variant = typeof chip === 'string' ? 'accent' : chip.variant
                      return (
                        <span key={idx} className={`px-2 py-1 rounded-full text-xs font-semibold border ${chipClass(variant)}`}>
                          {label}
                        </span>
                      )
                    })
                  )}
                </div>

                {/* Prominent Status Badges */}
                {Array.isArray(education.statusBadges) && education.statusBadges.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {education.statusBadges.map((b, idx) => {
                      const label = typeof b === 'string' ? b : b.label
                      const variant = typeof b === 'string' ? 'success' : b.variant
                      return (
                        <span key={idx} className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wide border ${chipClass(variant)}`}>
                          {label}
                        </span>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Description */}
              <p className="text-navy-base dark:text-navy-soft mb-5 leading-relaxed">
                {education.description}
              </p>

              {/* Key Courses */}
              {education.courses && education.courses.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs font-bold text-navy-dark dark:text-navy-lightest mb-3 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-8 h-0.5 bg-gradient-to-r from-navy-medium to-navy-deep"></span>
                    Key Courses
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {education.courses.map((c, idx) => (
                      <span key={idx} className={`px-2 py-1 rounded-md text-xs font-semibold border ${chipClass('neutral')}`}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Modules with Grades */}
              {education.modules && education.modules.length > 0 && (
                <div className="mb-5">
                  <h4 className="text-xs font-bold text-navy-dark dark:text-navy-lightest mb-3 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-8 h-0.5 bg-gradient-to-r from-navy-medium to-navy-deep"></span>
                    Core Modules & Grades
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {education.modules.map((m, idx) => (
                      <span key={idx} className={`px-2 py-1 rounded-md text-xs font-semibold border flex items-center gap-2 ${chipClass('neutral')}`}>
                        <span>{m.name}</span>
                        {m.grade && <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${gradeClass(m.grade)}`}>{m.grade}</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {education.results && education.results.length > 0 && (
                <div className="mt-5">
                  <h4 className="text-xs font-bold text-navy-dark dark:text-navy-lightest mb-3 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-8 h-0.5 bg-gradient-to-r from-navy-medium to-navy-deep"></span>
                    Exam Results
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {education.results.map((res, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm text-navy-base dark:text-navy-soft bg-navy-pale dark:bg-navy-base/50 rounded-xl p-3 border border-navy-soft dark:border-navy-base">
                        <span className="font-medium">{res.subject}</span>
                        <span className={`px-2 py-1 rounded-md text-xs font-bold ${gradeClass(res.grade)}`}>{res.grade}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements - 2 Column Grid */}
              {education.achievements && education.achievements.length > 0 && (
                <div className="mt-auto">
                  <h4 className="text-xs font-bold text-navy-dark dark:text-navy-lightest mb-3 uppercase tracking-widest flex items-center gap-2">
                    <span className="w-8 h-0.5 bg-gradient-to-r from-navy-medium to-navy-deep"></span>
                    Key Achievements
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {education.achievements.map((achievement, idx) => (
                      <div
                        key={idx}
                        className="relative group/item"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-navy-medium/10 to-navy-deep/10 rounded-xl opacity-0 group-hover/item:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-start gap-3 text-sm text-navy-base dark:text-navy-soft bg-navy-pale dark:bg-navy-base/50 rounded-xl p-3 border border-navy-soft dark:border-navy-base hover:border-accent/50 transition-all duration-300">
                          <span className="flex-shrink-0 w-6 h-6 rounded-lg bg-gradient-to-br from-navy-medium to-navy-deep flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                          </span>
                          <span className="flex-grow leading-snug">{achievement}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Decorative Corner Accent */}
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-navy-medium/10 to-transparent rounded-tr-full"></div>
      </div>
    </div>
  )
}

export default EducationCard
